//* Types imports
import type { IFinancialConfig } from "@/schemas/financial";
import type { ISale, ISaleOutputDTO } from "@/schemas/sales";

//* Schemas imports
import { MethodPaymentEnum } from "@/schemas/methodpayment";

type CalculateValueParams = {
  sale: ISale | ISaleOutputDTO;
  billingType: MethodPaymentEnum | string;
  installmentCount?: number;
};

type CalculationResult = {
  originalValue: number;
  finalValue: number;
  discountApplied?: number; // Percentage
  interestApplied?: number; // Percentage
  isCash: boolean;
};

export function calculatePaymentValues({
  sale,
  billingType,
  installmentCount = 1,
}: CalculateValueParams): CalculationResult {
  // 1. Get Financial Config
  let config: IFinancialConfig | undefined;

  if (sale.financialConfig) {
    config = {
      ...(typeof sale.productId === "object" && sale.productId
        ? (sale.productId as any).financialConfig
        : {}),
      ...sale.financialConfig,
    };
  } else if (
    typeof sale.productId === "object" &&
    sale.productId &&
    "financialConfig" in sale.productId
  ) {
    config = (sale.productId as any).financialConfig;
  }

  // * Pagamento integral (à vista)
  const isCash =
    billingType === MethodPaymentEnum.PIX ||
    billingType === MethodPaymentEnum.TED ||
    billingType === MethodPaymentEnum.BOLETO ||
    (billingType === MethodPaymentEnum.CREDIT_CARD && installmentCount === 1);

  let baseValue = 0;
  if (config) {
    baseValue = isCash
      ? config.cashPrice || config.installmentPrice || 0
      : config.installmentPrice || config.cashPrice || 0;
  }

  if (!config) {
    return {
      originalValue: baseValue,
      finalValue: baseValue,
      isCash,
    };
  }

  let finalValue = baseValue;

  //* Regra 3: Juros de Parcelamento (Calculado ANTES do desconto)
  let interestApplied = 0;
  if (
    !isCash &&
    config.interestFreeInstallments !== undefined &&
    installmentCount > config.interestFreeInstallments
  ) {
    const rate = config.interestRate || 0;
    //! Juros Simples
    const interestFactor = 1 + (rate * installmentCount) / 100;
    finalValue = finalValue * interestFactor;
    interestApplied = rate * installmentCount;
  }

  if (sale.managerDiscount) {
    // Aplica SOMENTE o desconto do gerente, ignorando os descontos padrão
    finalValue = finalValue * (1 - sale.managerDiscount / 100);
  } else {
    //* Regra 1: Desconto à Vista
    if (isCash && config.standardCashDiscount) {
      // Se for à vista, ignoramos os juros calculados acima (pois !isCash seria falso)
      finalValue = finalValue * (1 - config.standardCashDiscount / 100);
      return {
        originalValue: baseValue,
        finalValue: parseFloat(finalValue.toFixed(2)),
        discountApplied: config.standardCashDiscount,
        isCash,
      };
    }

    //* Regra 2: Desconto Parcelado Padrão
    if (!isCash && config.standardInstallmentDiscount && installmentCount > 1) {
      finalValue = finalValue * (1 - config.standardInstallmentDiscount / 100);
    }
  }

  return {
    originalValue: baseValue,
    finalValue: parseFloat(finalValue.toFixed(2)),
    discountApplied: sale.managerDiscount
      ? sale.managerDiscount
      : !isCash && config.standardInstallmentDiscount && installmentCount > 1
        ? config.standardInstallmentDiscount
        : undefined,
    interestApplied: interestApplied > 0 ? interestApplied : undefined,
    isCash,
  };
}
